import SEO from "../seo/SEO";
import { 
  FileText, 
  UserCheck, 
  BookOpen, 
  ShieldCheck, 
  Link2, 
  DollarSign, 
  AlertCircle, 
  Mail, 
  Globe, 
  HelpCircle 
} from "lucide-react";

export default function TermsConditions() {
  return (
    <div className="space-y-12 py-12 max-w-4xl mx-auto px-4">
      <SEO 
        title="Terms of Service - USMoneyAI" 
        description="Review the Terms of Service for USMoneyAI. Understand the terms, acceptable use, intellectual property, and guidelines governing the use of our website."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Terms of Service", url: "/terms" }
        ]}
      />

      {/* Header section */}
      <div className="space-y-3.5 text-left">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/20 bg-amber-500/10 text-xs font-semibold text-amber-400 select-none">
          <FileText className="w-3.5 h-3.5" />
          Terms of Service
        </div>
        <h1 className="text-3xl sm:text-4xl font-sans font-bold text-white tracking-tight">
          Terms of Service
        </h1>
        <p className="text-slate-400 text-sm font-light">
          Last Updated: June 2026
        </p>
      </div>

      <div className="space-y-8 text-left">
        {/* Welcome Section */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-amber-400 font-semibold text-lg">
            <UserCheck className="w-5 h-5 text-amber-500" />
            <h2>Welcome to USMoneyAI</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              Welcome to <strong>USMoneyAI</strong>. By using this website, you agree to the terms outlined below. If you do not agree with these terms, please do not use the website.
            </p>
          </div>
        </section>

        {/* Using Our Website */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <Globe className="w-5 h-5 text-amber-500" />
            <h2>Using Our Website</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              USMoneyAI provides free financial calculators, educational content, and planning tools designed to help users better understand personal finance topics.
            </p>
            <p>
              You may use our website for personal, non-commercial purposes, provided that your use does not violate any applicable laws or these terms.
            </p>
          </div>
        </section>

        {/* Calculator Results */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <HelpCircle className="w-5 h-5 text-amber-500" />
            <h2>Calculator Results</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              The calculators on USMoneyAI are intended to provide estimates based on the information entered by users.
            </p>
            <p>
              While we make every effort to keep our calculations accurate, results should not be treated as guarantees. Actual outcomes may vary depending on interest rates, taxes, fees, market conditions, and individual circumstances.
            </p>
            <p className="font-medium text-amber-300">
              Users are responsible for verifying information before making financial decisions.
            </p>
          </div>
        </section>

        {/* Educational Content */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <BookOpen className="w-5 h-5 text-amber-500" />
            <h2>Educational Content</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              Articles, guides, and other resources published on USMoneyAI are provided for informational purposes only.
            </p>
            <p>
              Nothing on this website should be considered financial, investment, tax, legal, or professional advice.
            </p>
            <p>
              If you need advice specific to your situation, please consult a qualified professional.
            </p>
          </div>
        </section>

        {/* Acceptable Use */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <h2>Acceptable Use</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p className="font-medium text-slate-200">
              When using USMoneyAI, you agree not to:
            </p>
            <ul className="space-y-2.5 pl-1 text-[15px]">
              {[
                "Use the website for unlawful purposes",
                "Attempt to interfere with website functionality or security",
                "Copy, scrape, or distribute large portions of content without permission",
                "Use automated systems that place excessive load on our servers",
                "Misrepresent information while interacting with the website"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Intellectual Property */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <ShieldCheck className="w-5 h-5 text-amber-500" />
            <h2>Intellectual Property</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              Unless otherwise stated, the content, calculators, design elements, logos, and materials available on USMoneyAI are the property of USMoneyAI and are protected by applicable copyright and intellectual property laws.
            </p>
            <p>
              You may view and use content for personal use, but you may not reproduce, republish, or distribute substantial portions of the website without permission.
            </p>
          </div>
        </section>

        {/* Third-Party Links */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <Link2 className="w-5 h-5 text-amber-500" />
            <h2>Third-Party Links</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              Some pages may contain links to third-party websites.
            </p>
            <p>
              These links are provided for convenience only. We are not responsible for the content, services, availability, or policies of external websites. Visiting third-party websites is done at your own discretion.
            </p>
          </div>
        </section>

        {/* Advertising */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <DollarSign className="w-5 h-5 text-amber-500" />
            <h2>Advertising</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              USMoneyAI may display advertisements provided by third-party advertising partners, including Google AdSense.
            </p>
            <p>
              Advertisements help support the operation of this website. The presence of advertising does not imply endorsement of any product, service, or company.
            </p>
          </div>
        </section>

        {/* Website Availability */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <Globe className="w-5 h-5 text-amber-500" />
            <h2>Website Availability</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              We do our best to keep USMoneyAI available and functioning properly. However, we cannot guarantee uninterrupted access at all times.
            </p>
            <p>
              From time to time, the website may experience maintenance, updates, technical issues, or temporary downtime.
            </p>
          </div>
        </section>

        {/* Limitation of Liability */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <h2>Limitation of Liability</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p className="font-semibold text-slate-200">
              USMoneyAI is provided on an &quot;as is&quot; basis.
            </p>
            <p>
              To the extent permitted by law, we are not responsible for any losses, damages, or decisions that may result from the use of this website, its calculators, or its content.
            </p>
            <p>
              Users remain responsible for their own financial decisions and actions.
            </p>
          </div>
        </section>

        {/* Changes to These Terms */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <FileText className="w-5 h-5 text-amber-500" />
            <h2>Changes to These Terms</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              We may update these Terms of Service from time to time.
            </p>
            <p>
              Any changes will be posted on this page along with an updated revision date. Continued use of the website after changes are posted constitutes acceptance of the updated terms.
            </p>
          </div>
        </section>

        {/* Contact Us */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-5">
          <h2 className="text-lg font-semibold text-white">Contact Us</h2>
          <p className="text-slate-300 text-base font-light">
            If you have questions regarding these Terms of Service, please contact us:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <a 
              href="mailto:earningtab72@gmail.com" 
              className="flex items-center gap-3 p-4 rounded-xl bg-[#121c31]/50 border border-slate-800 hover:border-slate-700 hover:bg-[#121c31] transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400 font-light font-mono">Email Support</div>
                <div className="text-[15px] font-medium text-white group-hover:text-amber-400 transition-colors">
                  earningtab72@gmail.com
                </div>
              </div>
            </a>
            
            <a 
              href="https://usmoneyai.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-3 p-4 rounded-xl bg-[#121c31]/50 border border-slate-800 hover:border-slate-700 hover:bg-[#121c31] transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400 font-light font-mono">Official Website</div>
                <div className="text-[15px] font-medium text-white group-hover:text-amber-400 transition-colors">
                  usmoneyai.com
                </div>
              </div>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
